import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon, CheckCircle2Icon } from 'lucide-react';
import { advertorials } from '../data/advertorials';
interface AdvertorialProps {
  serviceSlug: string;
  articleTheme?: string;
  onNavigate: (path: string) => void;
}
export function Advertorial({
  serviceSlug,
  articleTheme,
  onNavigate
}: AdvertorialProps) {
  // In a real app, we might use articleTheme to fetch a specific variant.
  // For now, we just use the default advertorial for the service.
  const articleData = advertorials.find(a => a.serviceSlug === serviceSlug);
  if (!articleData) {
    return <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4" data-id="element-98">
        <h1 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-99">
          Article not found
        </h1>
        <button onClick={() => onNavigate('/')} className="text-amber-600 hover:text-amber-700 font-medium flex items-center gap-2" data-id="element-100">
          Return Home <ChevronRightIcon className="w-4 h-4" data-id="element-101" />
        </button>
      </div>;
  }
  const handleCtaClick = () => {
    onNavigate(`/${serviceSlug}/quote-form`);
  };
  // Get current date for the article
  const currentDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  return <div className="min-h-screen bg-white font-sans" data-id="element-102">
      {/* Minimal Top Nav */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm" data-id="element-103">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-104">
          <a href="/" onClick={e => {
          e.preventDefault();
          onNavigate('/');
        }} className="text-xl md:text-2xl font-extrabold text-slate-900 tracking-tight" data-id="element-105">
            FindService<span className="text-amber-500" data-id="element-106">Quotes</span>
          </a>
          <button onClick={handleCtaClick} className="bg-[#7CB342] hover:bg-[#689F38] text-white px-4 py-2 rounded-md font-bold text-sm md:text-base transition-colors shadow-sm" data-id="element-107">
            See If You Qualify
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-id="element-108">
        {/* Article Header */}
        <div className="mb-8 text-center md:text-left" data-id="element-109">
          <div className="text-sm font-bold text-gray-500 tracking-widest uppercase mb-4" data-id="element-110">
            Advertorial •{' '}
            <span className="text-blue-600" data-id="element-111">{articleData.serviceName}</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 leading-tight mb-4 tracking-tight" data-id="element-112">
            {articleData.headline}
          </h1>
          <h2 className="text-xl md:text-2xl text-slate-600 font-medium leading-relaxed mb-6" data-id="element-113">
            {articleData.subtitle}
          </h2>
          <div className="flex items-center justify-center md:justify-start text-sm text-gray-600 font-medium" data-id="element-114">
            <span className="font-bold text-slate-900 mr-2" data-id="element-115">
              By FindServiceQuotes
            </span>
            <span className="mx-2 text-gray-300" data-id="element-116">|</span>
            <span data-id="element-117">{currentDate}</span>
          </div>
        </div>

        {/* Hero Image */}
        <div className="mb-10" data-id="element-118">
          <img src={articleData.heroImage} alt={articleData.headline} className="w-full h-auto rounded-lg shadow-md object-cover max-h-[500px]" data-id="element-119" />
        </div>

        {/* Article Body */}
        <article className="prose prose-lg max-w-none text-slate-800" data-id="element-120">
          {articleData.sections.map((section, index) => {
          switch (section.type) {
            case 'paragraph':
              return <p key={index} className="mb-6 text-lg leading-relaxed" data-id="element-121">
                    {section.text}
                  </p>;
            case 'subheading':
              return <h3 key={index} className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-5" data-id="element-122">
                    {section.text}
                  </h3>;
            case 'image':
              return <figure key={index} className="my-10" data-id="element-123">
                    <img src={section.src} alt={section.caption || ''} className="w-full rounded-lg shadow-sm" data-id="element-124" />
                    {section.caption && <figcaption className="text-center text-sm text-gray-500 mt-3 italic" data-id="element-125">
                        {section.caption}
                      </figcaption>}
                  </figure>;
            case 'quote':
              return <blockquote key={index} className="my-8 border-l-4 border-amber-500 bg-amber-50 p-6 rounded-r-lg" data-id="element-126">
                    <p className="text-xl italic font-medium text-slate-800 mb-2" data-id="element-127">
                      "{section.text}"
                    </p>
                    {section.author && <footer className="text-sm font-bold text-slate-600" data-id="element-128">
                        — {section.author}
                      </footer>}
                  </blockquote>;
            case 'callout':
              return <div key={index} className="my-8 bg-blue-50 border border-blue-100 p-6 rounded-lg" data-id="element-129">
                    <p className="text-blue-900 font-medium m-0" data-id="element-130">
                      {section.text}
                    </p>
                  </div>;
            case 'stats':
              return <div key={index} className="my-10 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-131">
                    {section.items?.map((item, i) => <div key={i} className="bg-slate-50 p-6 rounded-lg text-center border border-slate-100" data-id="element-132">
                        <div className="text-3xl font-extrabold text-amber-500 mb-2" data-id="element-133">
                          {item.value}
                        </div>
                        <div className="text-sm font-bold text-slate-700 uppercase tracking-wide" data-id="element-134">
                          {item.label}
                        </div>
                      </div>)}
                  </div>;
            case 'cta':
              return <div key={index} className="my-10 flex flex-col items-center justify-center bg-slate-50 p-8 rounded-xl border border-slate-200" data-id="element-135">
                    <h4 className="text-xl font-bold text-slate-900 mb-4 text-center" data-id="element-136">
                      Check Your Eligibility For {articleData.serviceName}{' '}
                      Savings
                    </h4>
                    <button onClick={handleCtaClick} className="w-full md:w-auto bg-[#7CB342] hover:bg-[#689F38] text-white text-lg font-bold py-4 px-10 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2" data-id="element-137">
                      See If You Qualify{' '}
                      <ChevronRightIcon className="w-5 h-5" data-id="element-138" />
                    </button>
                    <p className="text-xs text-gray-500 mt-4 flex items-center gap-1" data-id="element-139">
                      <CheckCircle2Icon className="w-3 h-3 text-green-500" data-id="element-140" />{' '}
                      Free, no-obligation quote
                    </p>
                  </div>;
            default:
              return null;
          }
        })}
        </article>

        {/* Footer Disclaimer */}
        <div className="mt-16 pt-8 border-t border-gray-200 text-xs text-gray-500 text-center" data-id="element-141">
          <p className="mb-2" data-id="element-142">
            THIS IS AN ADVERTORIAL AND NOT AN ACTUAL NEWS ARTICLE, BLOG, OR
            CONSUMER PROTECTION UPDATE.
          </p>
          <p data-id="element-143">
            FindServiceQuotes may receive compensation from the companies
            featured on this page. This compensation may affect the order,
            prominence, or location of specific listings.
          </p>
        </div>
      </main>
    </div>;
}